import PublicControlGate from './PublicControlGate';
import Attribution from "../../geo/Attribution";
import {useGeoLookup} from '../../geo/useGeoLookup';
import GeoRetry from '../../geo/GeoRetry';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Result,
  Spin,
  Descriptions,
  Tag,
  Typography,
  Space,
  Alert,
  Button,
  Divider,
  Row,
  Col,
  Statistic,
  Tooltip
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  QrcodeOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CarOutlined,
  MedicineBoxOutlined,
  PrinterOutlined,
  ShareAltOutlined,
  SafetyCertificateOutlined,
  LineChartOutlined,
  CompassOutlined
} from '@ant-design/icons';
import { MapCanvas as GoogleMap, RouteLayer as DirectionsRenderer, MapMarker as Marker, MapPolyline as Polyline, MapInfoWindow as InfoWindow, useMapLoader, fitMapBounds, circleSymbol } from "../../geo/Map";
import { routeEndpoints } from "../../geo/service";
import { formatLocationAddress } from "../../geo/addressLabel";
import axios from 'axios';
import './ValidarControlServicio.css';
import logoMogotax from '../../Assets/img/logo.png';

const { Title, Text, Paragraph } = Typography;

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px'
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: true,
};

// Marcadores de inicio (verde) y fin (rojo): se muestran aunque el traslado tenga un solo punto
const MARCADORES = {
  inicio: { label: 'I', color: '#52c41a', titulo: 'Inicio' },
  fin: { label: 'F', color: '#ff4d4f', titulo: 'Fin' },
};

const formatearHora = (timestamp) => {
  if (!timestamp) return '';
  try {
    return new Date(timestamp).toLocaleString('es-CO', { timeZone: 'America/Bogota' });
  } catch {
    return timestamp;
  }
};

const ValidarControlServicio = ({accessGrant,onVerificationRequired}) => {
  const { codigoQR } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [control, setControl] = useState(null);
  const [valido, setValido] = useState(false);
  const [error, setError] = useState(null);

  // Estados para el mapa
  const [center, setCenter] = useState({ lat: 4.6097, lng: -74.0817 });
  const [loadingMapa, setLoadingMapa] = useState(false);
  const [rutaData, setRutaData] = useState(null); // Guardar datos completos de la ruta
  const [marcadorAbierto, setMarcadorAbierto] = useState(null);
  const mapRef = useRef(null);
  const [mapRequested,setMapRequested]=useState(false);
  const [mapAuthorized,setMapAuthorized]=useState(false);
  const [mapError,setMapError]=useState('');
  const publicHeaders=accessGrant?{'X-Public-Control-Grant':accessGrant}:{};

  const { isLoaded, loadError, version } = useMapLoader({enabled:mapAuthorized});
  const lookupOptions={enabled:isLoaded && !!rutaData && mapAuthorized,qr:codigoQR,grant:accessGrant,version};
  const routeLookup=useGeoLookup('route',routeEndpoints(rutaData),lookupOptions);
  const startLookup=useGeoLookup('reverse',rutaData?.punto_inicio?{lat:Number(rutaData.punto_inicio.lat),lng:Number(rutaData.punto_inicio.lng)}:null,{...lookupOptions,endpoint:'inicio'});
  const endLookup=useGeoLookup('reverse',rutaData?.punto_fin?{lat:Number(rutaData.punto_fin.lat),lng:Number(rutaData.punto_fin.lng)}:null,{...lookupOptions,endpoint:'fin'});
  const directionsResponse=routeLookup.data;
  const addressLookups={inicio:startLookup,fin:endLookup};
  const direcciones=Object.fromEntries(Object.entries(addressLookups).flatMap(([name,lookup])=>[[name,formatLocationAddress(lookup.data?.result)||(lookup.busy?'Buscando dirección...':'Dirección no disponible')],[`${name}Attribution`,lookup.data?.result?.attribution]]));


  useEffect(() => {
    let active=true;
    setControl(null);setRutaData(null);setMapRequested(false);setMapAuthorized(false);setMarcadorAbierto(null);setMapError('');
    validarControlServicio(()=>active);
    return ()=>{active=false;};
    // eslint-disable-next-line
  }, [codigoQR]);

  // Cargar datos de ruta cuando control esté disponible
  useEffect(() => {
    let active=true;
    const gps = control?.gps;
    if (mapRequested && control?.traslado_id && (gps?.tiene_ruta || gps?.puntos_inicio > 0 || gps?.puntos_fin > 0)) {
      cargarRutaGPS(()=>active);
    }
    return ()=>{active=false;};
    // eslint-disable-next-line
  }, [control,mapRequested,accessGrant]);

  useEffect(()=>{if([routeLookup.error,startLookup.error,endLookup.error].some(e=>e?.code==='public_control_verification_required'))onVerificationRequired();},[routeLookup.error,startLookup.error,endLookup.error,onVerificationRequired]);

  const validarControlServicio = async (active) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/control-servicios/validar-publico/${encodeURIComponent(codigoQR)}`, {headers:publicHeaders}
      );

      if(!active())return;
      if (response.data.success && response.data.valido) {
        setControl(response.data.data);
        setValido(true);
      } else {
        setValido(false);
        setError(response.data.message || 'Control de servicio no válido');
      }
    } catch (err) {
      if(!active())return;
      if(err.response?.data?.code==='public_control_verification_required'){onVerificationRequired();return;}
      setValido(false);
      setError(
        err.response?.data?.message ||
        'Error al validar el control de servicio. Por favor, verifique el código e intente nuevamente.'
      );
    } finally {
      if(active())setLoading(false);
    }
  };

  const cargarRutaGPS = async (active) => {
    if (!control?.traslado_id) return;

    setLoadingMapa(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/gps/ruta-publica/${control.traslado_id}`, {headers:publicHeaders}
      );

      if(!active())return;
      if (response.data.success) {
        const data = response.data.data;

        // Centrar mapa en el punto de inicio
        if (data.punto_inicio) {
          setCenter({
            lat: parseFloat(data.punto_inicio.lat),
            lng: parseFloat(data.punto_inicio.lng)
          });
        } else if (data.ruta && data.ruta.length > 0) {
          // Fallback al primer punto del array
          setCenter({
            lat: parseFloat(data.ruta[0].lat),
            lng: parseFloat(data.ruta[0].lng)
          });
        }

        // Guardar datos completos de la ruta (incluye punto_inicio, punto_fin y ruta)
        setRutaData(data);setMapAuthorized(true);
      }
    } catch (err) {
      if(!active())return;
      if(err.response?.data?.code==='public_control_verification_required')onVerificationRequired();
      else setMapError('No se pudo cargar el recorrido. Intenta nuevamente.');
      setMapRequested(false);
    } finally {
      if(active())setLoadingMapa(false);
    }
  };

  // Puntos para marcadores, línea de respaldo y encuadre del mapa
  const puntosRuta = (rutaData?.ruta || []).map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }));
  const marcadores = ['inicio', 'fin']
    .map(tipo => ({ tipo, punto: rutaData?.[`punto_${tipo}`] }))
    .filter(({ punto }) => punto && punto.lat != null && punto.lng != null);

  const encuadrarMapa = useCallback(() => {
    const map = mapRef.current;
    if (!map || !rutaData) return;
    const puntos = [
      ...(rutaData.ruta || []),
      ...(['punto_inicio', 'punto_fin'].map(k => rutaData[k]).filter(Boolean)),
    ].map(p => ({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) }))
      .filter(p => !isNaN(p.lat) && !isNaN(p.lng));
    if (puntos.length === 0) return;
    if (puntos.length === 1) {
      map.setCenter(puntos[0]);
      map.setZoom(16);
      return;
    }
    fitMapBounds(map,puntos);
  }, [rutaData]);

  useEffect(() => {
    if (!directionsResponse) encuadrarMapa();
  }, [encuadrarMapa, directionsResponse]);

  const getEstadoAuditoriaTag = (estado) => {
    const estados = {
      recibido: { color: 'processing', icon: <CheckCircleOutlined />, text: 'RECIBIDO' },
      facturado: { color: 'success', icon: <CheckCircleOutlined />, text: 'FACTURADO' },
      rechazado: { color: 'error', icon: <CloseCircleOutlined />, text: 'RECHAZADO' },
      null: { color: 'default', icon: <ClockCircleOutlined />, text: 'SIN ESTADO' }
    };

    const estadoInfo = estados[estado] || estados.null;

    return (
      <Tag icon={estadoInfo.icon} color={estadoInfo.color} style={{ fontSize: '14px', padding: '4px 12px' }}>
        {estadoInfo.text}
      </Tag>
    );
  };

  const getEstadoServicioTag = (estado) => {
    return estado === 'terminado' ? (
      <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: '14px', padding: '4px 12px' }}>
        TERMINADO
      </Tag>
    ) : (
      <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontSize: '14px', padding: '4px 12px' }}>
        FALLIDO
      </Tag>
    );
  };

  const handleImprimir = () => {
    window.print();
  };

  const handleCompartir = async () => {
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Control de Servicio - ${control.codigo_pcs}`,
          text: `Validación de Control de Servicio MOGOTAX - ${control.nombre_paciente}`,
          url: url,
        });
      } catch (err) {
        console.log('Error al compartir:', err);
      }
    } else {
      // Fallback: copiar al portapapeles
      navigator.clipboard.writeText(url);
      alert('Enlace copiado al portapapeles');
    }
  };

  if (loading) {
    return (
      <div className="validar-control-container">
        <div className="validar-control-content-modern">
          <Card className="validar-control-card-modern">
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spin size="large" />
              <Title level={4} style={{ marginTop: 20 }}>
                Validando Control de Servicio...
              </Title>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!valido || error) {
    return (
      <div className="validar-control-container">
        <div className="validar-control-content-modern">
          <Card className="validar-control-card-modern">
            <Result
              status="error"
              title="Control de Servicio No Válido"
              subTitle={error}
              extra={[
                <Button type="primary" key="home" onClick={() => navigate('/')}>
                  Volver al Inicio
                </Button>,
              ]}
            />
          </Card>
        </div>
      </div>
    );
  }

  const puntosEnRuta = control?.gps?.puntos_en_ruta || 0;

  return (
    <div className="validar-control-container">
      <div className="validar-control-content-modern">
        <Card className="validar-control-card-modern">
          {/* Header */}
          <div className="control-header-modern">
            <div className="header-logo-modern">
              <img
                src={logoMogotax}
                alt="MOGOTAX SAS - Validación de Control de Servicio"
                className="company-logo"
              />
            </div>
            <div className="header-actions">
              <Space>
                <Tooltip title="Imprimir">
                  <Button
                    icon={<PrinterOutlined />}
                    onClick={handleImprimir}
                    className="no-print"
                  />
                </Tooltip>
                <Tooltip title="Compartir">
                  <Button
                    icon={<ShareAltOutlined />}
                    onClick={handleCompartir}
                    className="no-print"
                  />
                </Tooltip>
              </Space>
            </div>
          </div>

          <Divider />

          {/* Estado de Validación */}
          <Result
            status="success"
            icon={<SafetyCertificateOutlined style={{ color: '#52c41a' }} />}
            title="Documento Autenticado"
            subTitle={
              <Text>
                Este control de servicio ha sido verificado como auténtico y válido.
              </Text>
            }
            style={{ padding: '20px 0' }}
          />

          {/* Layout de 2 Columnas */}
          <Row gutter={[24, 24]}>
            {/* Columna Izquierda - Información del Servicio */}
            <Col xs={24} lg={12}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Códigos de Identificación */}
                <Card type="inner" title={<><QrcodeOutlined /> Códigos de Identificación</>} className="info-card">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Código QR">
                      <Text code copyable>{control.codigo_qr}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Código PCS">
                      <Text code copyable>{control.codigo_pcs}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Fecha del Servicio">
                      <Text strong>{control.fecha}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Datos del Conductor */}
                <Card type="inner" title={<><CarOutlined /> Datos del Conductor</>} className="info-card">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nombre">
                      <Text strong>{control.nombre_conductor}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Placa del Vehículo">
                      <Tag color="blue" style={{ fontSize: '14px' }}>{control.placa}</Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* Información del Paciente */}
                <Card type="inner" title={<><UserOutlined /> Información del Paciente</>} className="info-card">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Nombre">
                      <Text strong>{control.nombre_paciente}</Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Documento">
                      {control.tipo_documento_paciente} {control.numero_documento_paciente}
                    </Descriptions.Item>
                    <Descriptions.Item label="EPS">
                      {control.eps}
                    </Descriptions.Item>
                    {control.zona_paciente && (
                      <Descriptions.Item label="Zona">
                        {control.zona_paciente}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>

                {/* Información del Traslado */}
                <Card type="inner" title={<><MedicineBoxOutlined /> Detalles del Traslado</>} className="info-card">
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <EnvironmentOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      <Text strong>Origen</Text>
                    </div>
                    <Descriptions column={1} size="small" bordered={false}>
                      <Descriptions.Item label="Lugar">
                        {control.origen_lugar}
                      </Descriptions.Item>
                      <Descriptions.Item label="Ciudad">
                        {control.origen_ciudad}, {control.origen_departamento}
                      </Descriptions.Item>
                      <Descriptions.Item label="Hora">
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {control.origen_hora}
                      </Descriptions.Item>
                    </Descriptions>

                    <Divider style={{ margin: '12px 0' }} />

                    <div>
                      <EnvironmentOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                      <Text strong>Destino</Text>
                    </div>
                    <Descriptions column={1} size="small" bordered={false}>
                      <Descriptions.Item label="Lugar">
                        {control.destino_lugar}
                      </Descriptions.Item>
                      <Descriptions.Item label="Ciudad">
                        {control.destino_ciudad}, {control.destino_departamento}
                      </Descriptions.Item>
                      <Descriptions.Item label="Hora">
                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                        {control.destino_hora}
                      </Descriptions.Item>
                    </Descriptions>
                  </Space>
                </Card>
              </Space>
            </Col>

            {/* Columna Derecha - Mapa y Timeline */}
            <Col xs={24} lg={12}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Estadísticas GPS */}
                {control.gps && control.gps.tiene_ruta && (
                  <Card title={<><LineChartOutlined /> Estadísticas GPS</>} className="info-card">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title="Total Puntos GPS"
                          value={control.gps.total_puntos}
                          prefix={<EnvironmentOutlined />}
                          valueStyle={{ color: '#1890ff', fontSize: '1.5rem' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="Puntos en Ruta"
                          value={control.gps.puntos_en_ruta}
                          prefix={<CompassOutlined />}
                          valueStyle={{ color: '#52c41a', fontSize: '1.5rem' }}
                        />
                      </Col>
                    </Row>
                  </Card>
                )}

                {control.gps?.tiene_ruta && !mapRequested && <Card title="Mapa del recorrido" className="info-card"><Paragraph>Al abrir el mapa se cargarán el recorrido, la ruta por calles y las direcciones de inicio y fin.</Paragraph><Button type="primary" icon={<EnvironmentOutlined />} onClick={()=>{setMapError('');setMapRequested(true);}}>Ver mapa del recorrido</Button></Card>}
                {mapRequested && !isLoaded && !loadError && <Spin tip="Cargando recorrido…" />}
                {mapError && <Alert type="warning" showIcon message={mapError} />}
                <GeoRetry lookup={routeLookup} label="Reintentar ruta" />
                {loadError && <Alert type="warning" showIcon message={loadError.message} />}
                {/* Mapa GPS */}
                {control.gps && control.gps.tiene_ruta && mapRequested && isLoaded && (
                  <Card
                    title={<><EnvironmentOutlined /> Ruta del Traslado</>}
                    className="info-card"
                    extra={
                      <Tag color="blue">
                        {directionsResponse ? 'Ruta calculada' : 'Recorrido GPS'}
                      </Tag>
                    }
                  >
                    {loadingMapa ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin />
                      </div>
                    ) : (
                      <>
                        <GoogleMap
                          mapContainerStyle={mapContainerStyle}
                          center={center}
                          zoom={13}
                          options={mapOptions}
                          onLoad={(map) => { mapRef.current = map; encuadrarMapa(); }}
                          onUnmount={() => { mapRef.current = null; }}
                        >
                          {/* Ruta por calles cuando se pudo calcular */}
                          {directionsResponse && (
                            <DirectionsRenderer
                              directions={directionsResponse}
                              options={{
                                suppressMarkers: true,
                                polylineOptions: {
                                  strokeColor: '#1890ff',
                                  strokeOpacity: 0.8,
                                  strokeWeight: 5,
                                }
                              }}
                            />
                          )}

                          {/* Sin ruta calculada: línea con los puntos GPS reales */}
                          {!directionsResponse && puntosRuta.length >= 2 && (
                            <Polyline
                              path={puntosRuta}
                              options={{ strokeColor: '#1890ff', strokeOpacity: 0.8, strokeWeight: 4 }}
                            />
                          )}

                          {/* Inicio y fin siempre visibles, también con un solo punto (p. ej. fallido sin iniciar) */}
                          {marcadores.map(({ tipo, punto }) => (
                            <Marker
                              key={tipo}
                              position={{ lat: parseFloat(punto.lat), lng: parseFloat(punto.lng) }}
                              label={{ text: MARCADORES[tipo].label, color: '#fff', fontWeight: 'bold' }}
                              title={punto.descripcion || MARCADORES[tipo].titulo}
                              icon={{
                                path: circleSymbol(),
                                scale: 12,
                                fillColor: MARCADORES[tipo].color,
                                fillOpacity: 1,
                                strokeColor: '#fff',
                                strokeWeight: 2,
                              }}
                              onClick={() => setMarcadorAbierto(tipo)}
                            />
                          ))}
                          {marcadores.filter(({ tipo }) => tipo === marcadorAbierto).map(({ tipo, punto }) => (
                            <InfoWindow
                              key={`info-${tipo}`}
                              position={{ lat: parseFloat(punto.lat), lng: parseFloat(punto.lng) }}
                              onCloseClick={() => setMarcadorAbierto(null)}
                            >
                              <div>
                                <strong>{punto.descripcion || MARCADORES[tipo].titulo}</strong>
                                <div>{formatearHora(punto.timestamp)}</div>
                                <div style={{ marginTop: 4, maxWidth: 260 }}>
                                  📍 {direcciones[tipo] || 'Buscando dirección...'}<Attribution attribution={direcciones[`${tipo}Attribution`]} />
                                </div>
                              </div>
                            </InfoWindow>
                          ))}
                        </GoogleMap>
              <Attribution attribution={directionsResponse?.attribution} />
                        <Text type="secondary" style={{ fontSize: '12px', marginTop: 8, display: 'block' }}>
                          {directionsResponse
                            ? `📍 Ruta calculada por ${directionsResponse.provider === 'megatrack' ? 'MegatrackGeoApi' : 'Google Maps'} (${puntosEnRuta} puntos GPS registrados)`
                            : `📍 Puntos GPS registrados por el conductor (${puntosRuta.length})`}
                        </Text>
                        {marcadores.length > 0 && (
                          <Space direction="vertical" size={2} style={{ marginTop: 8 }}>
                            {marcadores.map(({ tipo, punto }) => (
                              <div key={`leyenda-${tipo}`} style={{ fontSize: '12px' }}>
                                <Text style={{ fontSize: '12px' }}>
                                  <Tag color={tipo === 'inicio' ? 'green' : 'red'}>{MARCADORES[tipo].label}</Tag>
                                  {punto.descripcion || MARCADORES[tipo].titulo} · {formatearHora(punto.timestamp)}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginLeft: 30 }}>
                                  📍 {direcciones[tipo] || 'Buscando dirección...'}<Attribution attribution={direcciones[`${tipo}Attribution`]} />
                                </Text>
                                <GeoRetry lookup={addressLookups[tipo]} label={`Reintentar dirección de ${tipo}`} />
                              </div>
                            ))}
                          </Space>
                        )}
                      </>
                    )}
                  </Card>
                )}

                {/* Información de Auditoría */}
                <Card type="inner" title={<><SafetyCertificateOutlined /> Información de Auditoría</>} className="info-card">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Estado del Servicio">
                      {getEstadoServicioTag(control.estado_servicio)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Estado de Auditoría">
                      {getEstadoAuditoriaTag(control.estado_auditoria)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Creado el">
                      {control.creado_en}
                    </Descriptions.Item>
                    {control.tag_auditoria && (
                      <Descriptions.Item label="Tag de Auditoría">
                        <Tag color="blue">{control.tag_auditoria}</Tag>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Space>
            </Col>
          </Row>

          <Divider />

          {/* Footer */}
          <Alert
            message="Documento Oficial Verificado"
            description={
              <div>
                <Paragraph style={{ margin: 0 }}>
                  <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                  Este documento ha sido verificado digitalmente por el sistema MOGOTAX.
                  La información aquí contenida es oficial y auténtica.
                </Paragraph>
                <Paragraph style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                  Para más información visite{' '}
                  <a href="https://mogotaxsas.com" target="_blank" rel="noopener noreferrer">
                    https://mogotaxsas.com
                  </a>
                </Paragraph>
              </div>
            }
            type="success"
            showIcon
            icon={<SafetyCertificateOutlined />}
          />
        </Card>
      </div>
    </div>
  );
};

export default function ProtectedPublicControl(){
  const {codigoQR}=useParams();
  return <PublicControlGate key={codigoQR} codigoQR={codigoQR}>{(grant,renew)=><ValidarControlServicio accessGrant={grant} onVerificationRequired={renew} />}</PublicControlGate>;
}
